//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import {
  DragSourceMonitor,
  DropTargetMonitor,
  useDrag,
  useDrop,
} from 'react-dnd';

import { useCombinedRefs } from '@hooks/useCombinedRefs';
import { DndItemType } from '@models/DndItemType';
import { Product } from '@models/IItemMetadata';
import { ProductTagItem } from '@uia/products/product-tags/ProductTagItem';

export interface ProductTagListItemProps {
  index: number;
  isDisabled: boolean;
  moveItem: (dragIndex: number, hoverIndex: number) => void;
  onEditProductTag: (productTag: Product) => void;
  onRemoveProductTag: (productTag: Product) => void;
  onDropItem: () => void;
  productTag: Product;
}

interface DragItem {
  index: number;
  productTag: Product;
  type: DndItemType.UIAProductTag;
}

export const ProductTagDraggableItem = ({
  index,
  isDisabled,
  moveItem,
  onDropItem,
  onEditProductTag,
  onRemoveProductTag,
  productTag,
}: ProductTagListItemProps) => {
  const [{ isDragging }, dragTarget] = useDrag({
    item: { productTag, index, type: DndItemType.UIAProductTag },
    canDrag: () => !isDisabled,
    collect: (monitor: DragSourceMonitor) => ({
      isDragging: monitor.isDragging(),
    }),
    end: (_, monitor) => {
      if (monitor.didDrop()) {
        onDropItem();
      }
    },
  });

  const [, dropTarget] = useDrop<DragItem, unknown, unknown>({
    accept: DndItemType.UIAProductTag,
    canDrop: () => !isDisabled,
    hover: (item: DragItem, monitor: DropTargetMonitor) => {
      if (!ref.current) {
        return;
      }

      const dragIndex = item.index;
      const hoverIndex = index;

      // If the item being dragged is the same as the item being hovered, do nothing
      if (dragIndex === hoverIndex) {
        return;
      }

      const hoverBoundingRect = ref.current.getBoundingClientRect();
      const hoverThresholdY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      const mousePosition = monitor.getClientOffset();
      const hoverClientY = (mousePosition?.y ?? 0) - hoverBoundingRect.top;

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverThresholdY) {
        return;
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverThresholdY) {
        return;
      }

      // Only perform the move when the mouse has crossed the move threshold of the item's height
      moveItem(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  const ref = useCombinedRefs<HTMLLIElement>(dragTarget, dropTarget);

  return (
    <li
      ref={ref}
      style={{
        opacity: isDragging ? 0.5 : 1,
        cursor: isDisabled ? 'default' : 'move',
      }}
    >
      <ProductTagItem
        onEditProductTag={onEditProductTag}
        onRemoveProductTag={onRemoveProductTag}
        productTag={productTag}
      />
    </li>
  );
};
